"use client";
import Spinner from "@/components/primitives/spinner";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { CheckIcon, TriangleAlertIcon } from "lucide-react";

export interface AnimatedButtonContentStateCopy {
  isInit: string;
  isPending: string;
  isError: string;
  isSuccess: string;
}

export interface AnimatedButtonContentCopy {
  errorPrefix: string;
  subject: AnimatedButtonContentStateCopy;
  action: AnimatedButtonContentStateCopy;
}

export interface AnimatedButtonContentProps {
  isInit: boolean;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  copy: AnimatedButtonContentCopy;
}

export default function AnimatedButtonContent({ isInit, isPending, isError, isSuccess, copy }: AnimatedButtonContentProps) {
  const horizontalTransition = {
    initial: { x: "-110%", opacity: 0, filter: "blur(2px)" },
    animate: { x: 0, opacity: 1, filter: "blur(0px)" },
    exit: { x: "-110%", opacity: 0, filter: "blur(2px)" },
  };

  return (
    <MotionConfig transition={{ duration: 0.2, type: "spring", bounce: 0.25 }}>
      <motion.div layout className="flex w-fit items-center overflow-hidden">
        <AnimatePresence mode="popLayout" initial={false}>
          {!isInit && (
            <motion.span {...horizontalTransition} layout key="typography-icon" className="mr-1.5">
              {isPending && <Spinner size={16} />}
              {isSuccess && <CheckIcon size={16} />}
              {isError && <TriangleAlertIcon size={16} className="animate-shake animate-thrice animate-duration-100 animate-ease-in-out" />}
            </motion.span>
          )}

          {isError && (
            <motion.span {...horizontalTransition} layout key="typography-error" className="mr-1.5 flex flex-shrink-0">
              {copy.errorPrefix}
            </motion.span>
          )}

          {(isPending || isInit || isError) && (
            <motion.span {...horizontalTransition} layout key="typography-action" className="mr-1.5 flex flex-shrink-0">
              {isInit ? copy.action.isInit : isPending ? copy.action.isPending : copy.action.isError}
            </motion.span>
          )}
          <motion.span layout key="typography-subject">
            {isSuccess ? copy.subject.isSuccess : copy.subject.isInit}
          </motion.span>
          {isSuccess && (
            <motion.span
              layout
              key="typography-action-success"
              initial={{ x: "110%", opacity: 0, filter: "blur(4px)" }}
              animate={{ x: 0, opacity: 1, filter: "blur(0px)" }}
              exit={{ x: "110%", opacity: 0, filter: "blur(4px)" }}
              className="ml-1.5"
            >
              {copy.action.isSuccess}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.div>
    </MotionConfig>
  );
}
